import axios, { type AxiosInstance } from 'axios';
import conf from '@/utils/conf';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';

const apiLogger = createLogger(LOG_NAMESPACES.API);

const RUBIAUTH_API_PATH_PREFIX = '/api/v2';
const RUBIAUTH_API_ENDPOINT_BASE = conf.apiHost + RUBIAUTH_API_PATH_PREFIX;

/**
 * Dedicated axios instance for Rubiauth V2 API.
 *
 * This instance is configured with `withCredentials: true` to allow
 * the browser to send and receive cookies (specifically `rubiauth_session`).
 *
 * IMPORTANT: This instance should ONLY be used for Rubiauth V2 endpoints.
 * All other API calls should use the main `api` instance from `@/api/instance`
 * which uses Bearer token authentication.
 */
const rubiauthAxiosInstance: AxiosInstance = axios.create({
  baseURL: RUBIAUTH_API_ENDPOINT_BASE,
  timeout: 155000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor — log requests
rubiauthAxiosInstance.interceptors.request.use(
  (config) => {
    apiLogger.debug(`🚀 Rubiauth V2 Request: ${config.method?.toUpperCase()} ${config.url}`, {
      headers: config.headers,
      data: config.data,
      params: config.params,
    });
    return config;
  },
  (error) => {
    apiLogger.error('❌ Rubiauth V2 request interceptor error:', error);
    return Promise.reject(error);
  },
);

// Response interceptor — handle errors and expired cookies
rubiauthAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      apiLogger.warn('Rubiauth V2 unauthorized - session may be expired', {
        url: error.config?.url,
        method: error.config?.method,
      });

      // Clear any expired rubiauth session cookies
      // This handles cases where the session-account cookie is expired but still present
      clearExpiredRubiauthCookies();
    }
    return Promise.reject(error);
  },
);

/**
 * Known Rubiauth session cookie names used during login/registration flows.
 */
const RUBIAUTH_COOKIE_NAMES = ['session-account', 'session-sign-in', 'session-create-account', 'rubiauth_session', 'session'];

/**
 * Paths the backend may have used when setting session cookies.
 * We attempt to clear cookies on each path because `document.cookie`
 * only deletes a cookie when the path (and domain) match exactly.
 */
const COOKIE_CLEAR_PATHS = ['/', '/api', '/api/v1', '/api/v1/user', '/api/v1/user/', '/api/v2', '/api/v2/rubiauth'];

function clearCookies(cookieNames: string[]) {
  try {
    const host = window.location.hostname;
    cookieNames.forEach((cookieName) => {
      for (const path of COOKIE_CLEAR_PATHS) {
        // biome-ignore lint/suspicious/noDocumentCookie: We need to clear expired session cookies
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
        // biome-ignore lint/suspicious/noDocumentCookie: We need to clear expired session cookies
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${host}`;
        // biome-ignore lint/suspicious/noDocumentCookie: We need to clear expired session cookies
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=.${host}`;
      }
    });
  } catch (error) {
    apiLogger.error('Failed to clear cookies', error);
  }
}

/**
 * Clear expired Rubiauth session cookies when we detect a 401.
 *
 * This function attempts to clear session cookies that may have expired
 * but are still present in the browser, causing authentication issues.
 */
export function clearExpiredRubiauthCookies() {
  clearCookies(RUBIAUTH_COOKIE_NAMES);
  apiLogger.info('Cleared potentially expired Rubiauth session cookies');
}

/**
 * Clear pre-authentication cookies after a successful login or TOTP verification.
 *
 * The backend leaves transitional cookies (e.g. `session-sign-in`, `session-account`)
 * in the browser after setting the real session cookies (`session-auth-access-token`,
 * `session-auth-refresh-token`). Some user-service endpoints appear to read the
 * wrong cookie when multiple are present, so we remove the transitional ones.
 */
export function clearPreAuthCookies() {
  clearCookies(['session-account', 'session-sign-in', 'session-create-account']);
  apiLogger.info('Cleared pre-authentication cookies');
}

export default rubiauthAxiosInstance;
export const rubiauthApi = rubiauthAxiosInstance;
